import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Check, X, AlertCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';

interface PricingPlan {
  id: string;
  name: string;
  description: string;
  image: string | null;
  defaultPrice: {
    id: string;
    currency: string;
    unit_amount: number;
    recurring: {
      interval: string;
      interval_count: number;
    } | null;
    trial_period_days: number | null;
  } | null;
  features: Array<{ name: string }>;
  metadata: Record<string, string>;
  popular?: boolean;
}

interface SubscriptionDetails {
  id: string;
  status: string;
  currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean;
  productName: string;
  productId: string;
}

interface PricingTableProps {
  pricingTableId?: string;
  publishableKey?: string;
  hasActiveSubscription?: boolean;
  subscriptionDetails?: SubscriptionDetails | null;
  setHasActiveSubscription?: (val: boolean) => void;
  setSubscriptionDetails?: (val: SubscriptionDetails | null) => void;
}

const PricingTable: React.FC<PricingTableProps> = ({
  pricingTableId,
  publishableKey,
  hasActiveSubscription: propHasActiveSubscription,
  subscriptionDetails: propSubscriptionDetails,
  setHasActiveSubscription: propSetHasActiveSubscription,
  setSubscriptionDetails: propSetSubscriptionDetails,
}) => {
  const { user, signIn, getToken, email } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [plans, setPlans] = useState<PricingPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showAuthMessage, setShowAuthMessage] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<PricingPlan | null>(null);
  // Local state only if not provided by props
  const [localHasActiveSubscription, setLocalHasActiveSubscription] = useState(false);
  const [localSubscriptionDetails, setLocalSubscriptionDetails] = useState<SubscriptionDetails | null>(null);
  const [checkingSubscription, setCheckingSubscription] = useState(false);

  // Use prop or local state
  const hasActiveSubscription =
    typeof propHasActiveSubscription === 'boolean' ? propHasActiveSubscription : localHasActiveSubscription;
  const subscriptionDetails =
    typeof propSubscriptionDetails !== 'undefined' ? propSubscriptionDetails : localSubscriptionDetails;
  const setHasActiveSubscription = propSetHasActiveSubscription || setLocalHasActiveSubscription;
  const setSubscriptionDetails = propSetSubscriptionDetails || setLocalSubscriptionDetails;

  // Format currency for display
  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits: 0,
    }).format(amount / 100);
  };
  
  // Format date for display, fallback if invalid
  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    if (isNaN(date.getTime()) || date.getTime() < 24 * 60 * 60 * 1000) return null; // treat epoch/invalid as null
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };
  
  // Check for checkout status in URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const checkoutStatus = params.get('checkout');
    
    if (checkoutStatus === 'success') {
      setShowSuccessMessage(true);
      // Clear the checkout param from URL
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('checkout');
      window.history.replaceState({}, '', newUrl);
      
      // Scroll to pricing section to show subscription benefits
      setTimeout(() => {
        document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
      }, 500);
    }
  }, [location]);

  // Only fetch subscription status if not provided by props AND this component is mounted as a page (standalone)
  // Do NOT fetch on prop changes, only on initial mount (and only if not controlled)
  // This disables all polling or repeated backend calls
  const didCheck = React.useRef(false);
  // Reset subscription check flag when user or email changes (login/logout)
  useEffect(() => {
    didCheck.current = false;
  }, [user, email]);
  const [rateLimitCooldown, setRateLimitCooldown] = useState(false);
  // Reset check flag when user/email changes
  // Run subscription check when user/email changes
  useEffect(() => {
    didCheck.current = false;
  }, [user, email]);
  // Fetch subscription once per user when login/logout occurs
  useEffect(() => {
    if (typeof propHasActiveSubscription !== 'undefined' && typeof propSubscriptionDetails !== 'undefined') return;
    // Only run on first mount
    if (didCheck.current) return;
    const sessionKey = `subscriptionChecked_${email || ''}`;
    if (!user || !email || rateLimitCooldown || sessionStorage.getItem(sessionKey)) return;
    didCheck.current = true;
    sessionStorage.setItem(sessionKey, 'true');
    console.log('[PricingTable] Checking subscription status for', email);
    const checkSubscriptionStatus = async () => {
      try {
        setCheckingSubscription(true);
        const token = await getToken();
        // Fetch subscription status via GET with email in query to ensure correct lookup
        const url = `/api/check-subscription?email=${encodeURIComponent(email || '')}`;
        console.log('[PricingTable] Fetching subscription status with GET query', url);
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (response.status === 429) {
          toast({
            title: 'Rate Limit Exceeded',
            description: 'Too many requests to our payment provider. Please wait a minute before trying again.',
            variant: 'destructive',
          });
          setRateLimitCooldown(true);
          setTimeout(() => {
            setRateLimitCooldown(false);
            didCheck.current = false;
            sessionStorage.removeItem(sessionKey);
          }, 60000); // 1 minute cooldown
          return;
        }
        if (!response.ok) {
          console.error('Failed to check subscription status:', await response.text());
          return;
        }
        const data = await response.json();
        setHasActiveSubscription(data.hasActiveSubscription);
        setSubscriptionDetails(data.subscription);
        if (data.hasActiveSubscription) {
          sessionStorage.removeItem('pendingStripeCheckout');
          sessionStorage.removeItem('pendingPlanName');
          sessionStorage.removeItem('checkoutExplicitlyRequested');
        }
      } catch (err) {
        console.error('Error checking subscription status:', err);
        toast({
          title: 'Network Error',
          description: 'Could not connect to the server. Please try again later.',
          variant: 'destructive',
        });
      } finally {
        setCheckingSubscription(false);
      }
    };
    checkSubscriptionStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, email]);

  // Fetch pricing plans from our API
  useEffect(() => {
    const fetchPricingPlans = async () => {
      try {
        setLoading(true);
        const response = await fetch('/api/get-pricing-plans');
        console.log('[PricingTable] /api/get-pricing-plans response:', response);
        if (!response.ok) {
          throw new Error(`Error fetching plans: ${response.status}`);
        }
        const data = await response.json();
        console.log('[PricingTable] /api/get-pricing-plans data:', data);
        // Add a "popular" flag to the middle plan if there are 3 plans
        if (data.plans && data.plans.length === 3) {
          data.plans[1].popular = true;
        } else if (data.plans && data.plans.length > 0) {
          // Mark the first non-free plan as popular
          const nonFreePlans = data.plans.filter(plan => 
            plan.defaultPrice && plan.defaultPrice.unit_amount > 0
          );
          if (nonFreePlans.length > 0) {
            const popularPlanIndex = data.plans.findIndex(
              plan => plan.id === nonFreePlans[0].id
            );
            if (popularPlanIndex >= 0) {
              data.plans[popularPlanIndex].popular = true;
            }
          }
        }
        console.log('[PricingTable] plans to be set:', data.plans);
        setPlans(data.plans || []);
      } catch (err) {
        console.error('Failed to fetch pricing plans:', err);
        setError('Failed to load pricing information. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchPricingPlans();
  }, []);

  // Check if the user just logged in with a pending checkout
  useEffect(() => {
    if (user) {
      const pendingCheckoutId = sessionStorage.getItem('pendingStripeCheckout');
      const redirectToMain = sessionStorage.getItem('redirectToMainAfterAuth');
      const checkoutRequested = sessionStorage.getItem('checkoutExplicitlyRequested');
      
      // Clear the redirect flag first
      if (redirectToMain) {
        sessionStorage.removeItem('redirectToMainAfterAuth');
      }
      
      // Only process checkout if user doesn't have an active subscription
      if (!hasActiveSubscription) {
        // If we have a pending checkout and user is logged in AND they explicitly requested checkout
        if (pendingCheckoutId && checkoutRequested && !redirectToMain) {
          console.log('User logged in with pending checkout, redirecting to Stripe');
          setShowAuthMessage(false);
          sessionStorage.removeItem('checkoutExplicitlyRequested'); // Clear the flag
          handlePriceSelection(pendingCheckoutId);
        } else if (pendingCheckoutId && redirectToMain) {
          // User wants to go back to main page after login
          sessionStorage.removeItem('pendingStripeCheckout');
          navigate('/');
        } else if (pendingCheckoutId) {
          // We have a pendingCheckoutId but no explicit request - clear it to prevent auto redirects
          sessionStorage.removeItem('pendingStripeCheckout');
        }
      }
    }
  }, [user, navigate, hasActiveSubscription]);

  const handleStartTrial = (plan: PricingPlan) => {
    setSelectedPlan(plan);
    
    // Check if user already has an active subscription
    if (user && hasActiveSubscription) {
      toast({
        title: "You already have an active subscription",
        description: `You're currently subscribed to ${subscriptionDetails?.productName}. You can only have one active subscription at a time.`,
        variant: "destructive",
      });
      return;
    }
    
    // Set flag that user explicitly requested checkout
    sessionStorage.setItem('checkoutExplicitlyRequested', 'true');
    
    if (!user) {
      // Store the selected plan for post-auth checkout
      if (plan.defaultPrice) {
        // Set flag to redirect to main page after auth instead of Stripe checkout
        sessionStorage.setItem('redirectToMainAfterAuth', 'true');
        sessionStorage.setItem('pendingStripeCheckout', plan.defaultPrice.id);
        sessionStorage.setItem('pendingPlanName', plan.name);
      }
      setShowAuthMessage(true);
    } else if (plan.defaultPrice) {
      handlePriceSelection(plan.defaultPrice.id);
    }
  };

  const handleLoginClick = () => {
    // Store current URL to return here after auth
    sessionStorage.setItem('authRedirectUrl', window.location.pathname);
    
    // Hide the auth message
    setShowAuthMessage(false);
    
    // Redirect to Clerk's sign-in page
    navigate('/sign-in');
  };

  const handlePriceSelection = async (priceId: string) => {
    // If user is not logged in, show authentication message
    if (!user) {
      console.log('User not logged in, showing auth message');
      setShowAuthMessage(true);
      return;
    }
    
    // Check if user already has an active subscription
    if (hasActiveSubscription) {
      toast({
        title: "You already have an active subscription",
        description: `You're currently subscribed to ${subscriptionDetails?.productName}. You can only have one active subscription at a time.`,
        variant: "destructive",
      });
      return;
    }
    
    // User is logged in, proceed to checkout
    try {
      console.log('Creating checkout session for price:', priceId);
      const token = await getToken();
      const response = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          priceId,
          successUrl: `${window.location.origin}${location.pathname}?checkout=success`,
          cancelUrl: `${window.location.origin}${location.pathname}`,
          email, // Pass the actual user email to backend
        }),
      });

      if (!response.ok) {
        throw new Error(`Error creating checkout session: ${response.status}`);
      }

      const data = await response.json();
      
      // Redirect to Stripe Checkout
      if (data.url) {
        console.log('Redirecting to Stripe checkout');
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL returned from API');
      }
    } catch (error) {
      console.error('Error redirecting to checkout:', error);
      toast({
        title: "Checkout Error",
        description: "Sorry, we encountered an error setting up the checkout. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Show loading skeleton while fetching plans
  if (loading) {
    return (
      <section id="pricing" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-8 text-center">
          <p className="text-lg text-gray-600 mb-12 max-w-3xl mx-auto">
            Choose the plan that works best for you and your trading needs.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-8">
            {[1, 2, 3].map((item) => (
              <div key={item} className="bg-gray-100 rounded-xl p-8 animate-pulse h-96"></div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  // Show "Coming Soon" message if failed to load plans
  if (error) {
    return (
      <section id="pricing" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Coming Soon</h2>
          <p className="text-lg text-gray-600 mb-12 max-w-3xl mx-auto">
            We're working on our pricing plans. Check back soon for more details.
          </p>
          <div className="flex justify-center">
            <Card className="w-full max-w-md">
              <CardContent className="pt-6">
                <div className="flex flex-col items-center p-6">
                  <AlertCircle className="h-12 w-12 text-blue-500 mb-4" />
                  <p className="text-xl font-semibold">Pricing options will be available soon</p>
                  <p className="mt-2 text-center text-gray-500">
                    We're finalizing our subscription plans to provide you with the best value.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="pricing" className="py-10 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-8 text-center">
        
        {/* Removed duplicate active-subscription banner per design: top page shows status */}
        
        <h2 className="text-3xl md:text-4xl font-bold mb-6">Pricing Plans</h2>
        <p className="text-lg text-gray-600 mb-12 max-w-3xl mx-auto">
          Choose the plan that works best for you and your trading needs.
        </p>
        
        {showSuccessMessage && (
          <div className="mb-8 bg-green-50 border border-green-200 rounded-lg p-6 text-left">
            <h3 className="text-xl font-bold text-green-800 mb-2">Subscription Activated!</h3>
            <p className="text-green-700">
              Thank you for subscribing! Your account has been upgraded and you now have access to all premium features.
            </p>
          </div>
        )}
        
        {/* Authentication Required Message */}
        {showAuthMessage && (
          <div className="mb-8 bg-blue-50 border-l-4 border-blue-500 p-6 rounded-lg text-left">
            <h3 className="text-xl font-bold text-blue-800 mb-2">Authentication Required</h3>
            <p className="text-blue-700 mb-4">
              Please sign in with Google to continue with your {selectedPlan?.name || 'subscription'}.
              After signing in, you'll be redirected back to the main page.
            </p>
            <button 
              onClick={handleLoginClick}
              className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-6 rounded-md transition-colors"
            >
              Sign In with Google
            </button>
            <button 
              onClick={() => setShowAuthMessage(false)} 
              className="ml-3 text-blue-600 hover:text-blue-800 font-medium py-2 px-4"
            >
              Cancel
            </button>
          </div>
        )}
        
        {/* Custom Pricing Table */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-8">
          {plans.map((plan) => (
            <Card 
              key={plan.id}
              className={`flex flex-col relative ${plan.popular ? 'border-blue-500 shadow-lg transform scale-[1.02]' : 'border-gray-200'}`}
            >
              {plan.popular && (
                <Badge className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-blue-500">
                  Popular Choice
                </Badge>
              )}
              <CardHeader className="pb-4">
                <CardTitle className="text-xl">{plan.name}</CardTitle>
                {plan.defaultPrice && (
                  <div className="mt-4">
                    <span className="text-4xl font-bold">{formatCurrency(plan.defaultPrice.unit_amount, plan.defaultPrice.currency)}</span>
                    {plan.defaultPrice.recurring && (
                      <span className="text-gray-500">
                        /{plan.defaultPrice.recurring.interval}
                      </span>
                    )}
                  </div>
                )}
                <CardDescription className="mt-4">{plan.description}</CardDescription>
              </CardHeader>
              <CardContent className="flex-grow">
                <ul className="space-y-2 text-left">
                  {plan.features?.map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <Check className="h-5 w-5 text-green-500 mr-2 mt-0.5" />
                      <span>{feature.name}</span>
                    </li>
                  ))}
                  
                  {/* Add some placeholder features if no features are defined */}
                  {(!plan.features || plan.features.length === 0) && (
                    <>
                      <li className="flex items-start">
                        <Check className="h-5 w-5 text-green-500 mr-2 mt-0.5" />
                        <span>Basic trading analytics</span>
                      </li>
                      <li className="flex items-start">
                        <Check className="h-5 w-5 text-green-500 mr-2 mt-0.5" />
                        <span>Strategy recommendations</span>
                      </li>
                    </>
                  )}
                </ul>
              </CardContent>
              <CardFooter className="pt-4">
                {/* Show cancel for active plan, update for others if user has active subscription */}
                {user && hasActiveSubscription && subscriptionDetails ? (
                  plan.id === subscriptionDetails.productId ? (
                    // Current active plan - indicate current subscription instead of cancel
                    <Button
                      className="w-full bg-green-600 text-white font-medium cursor-default"
                      disabled
                    >
                      Current Subscription
                    </Button>
                  ) : (
                    <Button
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium"
                      onClick={async () => {
                        const confirmMsg = `Are you sure you want to change your subscription to ${plan.name}?\n\nYour billing will be prorated automatically by Stripe. You may see a charge or credit on your next invoice depending on the price difference and time left in your current period.`;
                        if (!window.confirm(confirmMsg)) return;
                        try {
                          const token = await getToken();
                          const response = await fetch('/api/update-subscription', {
                            method: 'POST',
                            headers: {
                              'Content-Type': 'application/json',
                              'Authorization': `Bearer ${token}`
                            },
                            credentials: 'include',
                            body: JSON.stringify({
                              email,
                              subscriptionId: subscriptionDetails.id,
                              newPriceId: plan.defaultPrice?.id
                            })
                          });
                          if (!response.ok) {
                            const errorText = await response.text();
                            toast({
                              title: 'Update Failed',
                              description: errorText || 'Unable to update your subscription. Please try again.',
                              variant: 'destructive',
                            });
                            return;
                          }
                          const data = await response.json();
                          toast({
                            title: 'Subscription Updated',
                            description: `Your subscription has been changed to ${plan.name}.`,
                            variant: 'default',
                          });
                          // Update state via provided setters
                          setHasActiveSubscription(true);
                          setSubscriptionDetails(data.subscription);
                        } catch (err) {
                          toast({
                            title: 'Error',
                            description: 'An error occurred while updating your subscription.',
                            variant: 'destructive',
                          });
                        }
                      }}
                    >
                      {checkingSubscription ? 'Updating...' : 'Update Subscription'}
                    </Button>
                  )
                ) : (
                  <Button 
                    onClick={() => handleStartTrial(plan)}
                    className={`w-full ${plan.popular ? 'bg-blue-600 hover:bg-blue-700' : ''}`}
                  >
                    {plan.defaultPrice?.trial_period_days ? 'Start Trial' : 'Subscribe'}
                  </Button>
                )}
              </CardFooter>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PricingTable;